import React, { useState, useEffect } from "react";
import { testPocketBaseConnection } from "../lib/pocketbase";
import { authService } from "../lib/auth";

const PocketBaseTestPage: React.FC = () => {
  const [connectionStatus, setConnectionStatus] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const testConnection = async () => {
    setIsLoading(true);
    const result = await testPocketBaseConnection();
    setConnectionStatus(result);
    setIsLoading(false);
  };

  useEffect(() => {
    testConnection();
  }, []);

  if (!connectionStatus) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Testing connection...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            PocketBase Connection Test
          </h1>

          <div className="space-y-6">
            {/* Connection Status */}
            <div
              className={`p-4 rounded-lg border ${
                connectionStatus.success
                  ? "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800"
                  : "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800"
              }`}>
              <h2 className="font-semibold mb-2">
                {connectionStatus.success
                  ? "✅ Connected"
                  : "❌ Connection Failed"}
              </h2>
              {connectionStatus.message && (
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  {connectionStatus.message}
                </p>
              )}
              {connectionStatus.error && (
                <p className="text-sm text-red-600 dark:text-red-400">
                  Error: {connectionStatus.error.message}
                </p>
              )}
            </div>

            {/* Missing Collections */}
            {connectionStatus.missingCollections &&
              connectionStatus.missingCollections.length > 0 && (
                <div className="p-4 rounded-lg border border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800">
                  <h3 className="font-semibold mb-2">⚠️ Missing Collections</h3>
                  <p className="text-sm mb-2">
                    The following collections need to be created in PocketBase:
                  </p>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    {connectionStatus.missingCollections.map(
                      (collection: string) => (
                        <li key={collection} className="font-mono">
                          {collection}
                        </li>
                      )
                    )}
                  </ul>
                  <div className="mt-3">
                    <a
                      href="http://127.0.0.1:8090/_/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline">
                      Open PocketBase Admin →
                    </a>
                  </div>
                </div>
              )}

            {/* Setup Instructions */}
            {connectionStatus.missingCollections &&
              connectionStatus.missingCollections.length > 0 && (
                <div className="p-4 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
                  <h3 className="font-semibold mb-2">
                    📋 Detailed Collection Setup Instructions
                  </h3>

                  {/* Users Collection */}
                  <div className="mb-6">
                    <h4 className="font-medium text-lg mb-3">
                      1. Users Collection (Required)
                    </h4>
                    <ol className="list-decimal list-inside text-sm space-y-2 ml-4">
                      <li>
                        Open{" "}
                        <a
                          href="http://127.0.0.1:8090/_/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 dark:text-blue-400 hover:underline">
                          PocketBase Admin
                        </a>
                      </li>
                      <li>Click "Collections" in the left sidebar</li>
                      <li>Click "New collection"</li>
                      <li>
                        Set <strong>Name</strong>:{" "}
                        <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                          users
                        </code>
                      </li>
                      <li>
                        Set <strong>Type</strong>:{" "}
                        <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                          Auth collection
                        </code>{" "}
                        (very important!)
                      </li>
                      <li>Click "Create"</li>
                      <li>
                        The collection will be created with basic fields (email,
                        name, password)
                      </li>
                    </ol>
                  </div>

                  {/* Other Collections */}
                  <div className="mb-6">
                    <h4 className="font-medium text-lg mb-3">
                      2. Other Collections (Optional)
                    </h4>
                    <p className="text-sm mb-3">
                      Create these as "Base collections" (not Auth collections):
                    </p>

                    <div className="space-y-4">
                      {/* Transactions */}
                      <div className="border border-gray-200 rounded-lg p-3">
                        <h5 className="font-medium mb-2">
                          Transactions Collection
                        </h5>
                        <ol className="list-decimal list-inside text-xs space-y-1 ml-4">
                          <li>
                            Name:{" "}
                            <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                              transactions
                            </code>
                          </li>
                          <li>
                            Type:{" "}
                            <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                              Base collection
                            </code>
                          </li>
                          <li>
                            Add fields:
                            <ul className="list-disc list-inside ml-4 mt-1">
                              <li>
                                <code>userId</code> (Relation to users,
                                required)
                              </li>
                              <li>
                                <code>amount</code> (Number, required)
                              </li>
                              <li>
                                <code>description</code> (Text, required)
                              </li>
                              <li>
                                <code>category</code> (Text, required)
                              </li>
                              <li>
                                <code>type</code> (Select: income/expense,
                                required)
                              </li>
                              <li>
                                <code>date</code> (Date, required)
                              </li>
                            </ul>
                          </li>
                        </ol>
                      </div>

                      {/* Assets */}
                      <div className="border border-gray-200 rounded-lg p-3">
                        <h5 className="font-medium mb-2">Assets Collection</h5>
                        <ol className="list-decimal list-inside text-xs space-y-1 ml-4">
                          <li>
                            Name:{" "}
                            <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                              assets
                            </code>
                          </li>
                          <li>
                            Type:{" "}
                            <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                              Base collection
                            </code>
                          </li>
                          <li>
                            Add fields:
                            <ul className="list-disc list-inside ml-4 mt-1">
                              <li>
                                <code>userId</code> (Relation to users,
                                required)
                              </li>
                              <li>
                                <code>name</code> (Text, required)
                              </li>
                              <li>
                                <code>type</code> (Text, required)
                              </li>
                              <li>
                                <code>balance</code> (Number, required)
                              </li>
                              <li>
                                <code>isLiquid</code> (Bool, default: false)
                              </li>
                            </ul>
                          </li>
                        </ol>
                      </div>

                      {/* IncomeSources */}
                      <div className="border border-gray-200 rounded-lg p-3">
                        <h5 className="font-medium mb-2">
                          IncomeSources Collection
                        </h5>
                        <ol className="list-decimal list-inside text-xs space-y-1 ml-4">
                          <li>
                            Name:{" "}
                            <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                              incomeSources
                            </code>
                          </li>
                          <li>
                            Type:{" "}
                            <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                              Base collection
                            </code>
                          </li>
                          <li>
                            Add fields:
                            <ul className="list-disc list-inside ml-4 mt-1">
                              <li>
                                <code>userId</code> (Relation to users,
                                required)
                              </li>
                              <li>
                                <code>name</code> (Text, required)
                              </li>
                              <li>
                                <code>amount</code> (Number, required)
                              </li>
                              <li>
                                <code>frequency</code> (Select: monthly/yearly,
                                required)
                              </li>
                            </ul>
                          </li>
                        </ol>
                      </div>

                      {/* Debts */}
                      <div className="border border-gray-200 rounded-lg p-3">
                        <h5 className="font-medium mb-2">Debts Collection</h5>
                        <ol className="list-decimal list-inside text-xs space-y-1 ml-4">
                          <li>
                            Name:{" "}
                            <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                              debts
                            </code>
                          </li>
                          <li>
                            Type:{" "}
                            <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                              Base collection
                            </code>
                          </li>
                          <li>
                            Add fields:
                            <ul className="list-disc list-inside ml-4 mt-1">
                              <li>
                                <code>userId</code> (Relation to users,
                                required)
                              </li>
                              <li>
                                <code>name</code> (Text, required)
                              </li>
                              <li>
                                <code>balance</code> (Number, required)
                              </li>
                              <li>
                                <code>interestRate</code> (Number, required)
                              </li>
                              <li>
                                <code>minimumPayment</code> (Number, required)
                              </li>
                            </ul>
                          </li>
                        </ol>
                      </div>

                      {/* Partners */}
                      <div className="border border-gray-200 rounded-lg p-3">
                        <h5 className="font-medium mb-2">
                          Partners Collection
                        </h5>
                        <ol className="list-decimal list-inside text-xs space-y-1 ml-4">
                          <li>
                            Name:{" "}
                            <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                              partners
                            </code>
                          </li>
                          <li>
                            Type:{" "}
                            <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                              Base collection
                            </code>
                          </li>
                          <li>
                            Add fields:
                            <ul className="list-disc list-inside ml-4 mt-1">
                              <li>
                                <code>userId</code> (Relation to users,
                                required)
                              </li>
                              <li>
                                <code>name</code> (Text, required)
                              </li>
                              <li>
                                <code>monthlyIncome</code> (Number, required)
                              </li>
                              <li>
                                <code>monthlyExpenses</code> (Number, required)
                              </li>
                              <li>
                                <code>liquidAssets</code> (Number, required)
                              </li>
                              <li>
                                <code>isActive</code> (Bool, default: true)
                              </li>
                            </ul>
                          </li>
                        </ol>
                      </div>
                    </div>
                  </div>

                  {/* Tips */}
                  <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <h5 className="font-medium mb-2">💡 Tips:</h5>
                    <ul className="text-xs space-y-1">
                      <li>
                        • Start with just the <strong>users</strong> collection
                        to test authentication
                      </li>
                      <li>
                        • Make sure <strong>users</strong> is an "Auth
                        collection" (not Base collection)
                      </li>
                      <li>
                        • All other collections should be "Base collections"
                      </li>
                      <li>
                        • For relation fields, select the <strong>users</strong>{" "}
                        collection as the target
                      </li>
                      <li>
                        • After creating collections, test the connection again
                      </li>
                      <li>
                        • Use passwords with 8+ characters (not 6-digit PINs)
                      </li>
                    </ul>
                  </div>

                  {/* Quick Reference */}
                  <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <h5 className="font-medium mb-2">📋 Quick Reference:</h5>
                    <div className="text-xs">
                      <p className="mb-2">
                        <strong>Required Collections:</strong>
                      </p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>
                          <code>users</code> (Auth collection)
                        </li>
                      </ul>
                      <p className="mb-2 mt-3">
                        <strong>Optional Collections:</strong>
                      </p>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>
                          <code>transactions</code> (Base collection)
                        </li>
                        <li>
                          <code>assets</code> (Base collection)
                        </li>
                        <li>
                          <code>incomeSources</code> (Base collection)
                        </li>
                        <li>
                          <code>debts</code> (Base collection)
                        </li>
                        <li>
                          <code>partners</code> (Base collection)
                        </li>
                      </ul>
                      <p className="mt-3 text-green-700 dark:text-green-300">
                        <strong>
                          See DETAILED_SETUP_GUIDE.md for complete instructions
                        </strong>
                      </p>
                    </div>
                  </div>
                </div>
              )}

            {/* Test Authentication */}
            <div className="p-4 rounded-lg border border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="font-semibold mb-2">🔐 Test Authentication</h3>
              <p className="text-sm mb-3">
                Once collections are set up, you can test the authentication
                system.
              </p>
              <button
                onClick={testConnection}
                disabled={isLoading}
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50">
                {isLoading ? "Testing..." : "Re-test Connection"}
              </button>
            </div>

            {/* Current Auth Status */}
            <div className="p-4 rounded-lg border border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="font-semibold mb-2">👤 Authentication Status</h3>
              <p className="text-sm">
                Current user:{" "}
                {authService.getCurrentUser()?.name || "Not signed in"}
              </p>
              <p className="text-sm">
                Authenticated: {authService.isAuthenticated() ? "Yes" : "No"}
              </p>
            </div>

            {/* Navigation */}
            <div className="p-4 rounded-lg border border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
              <h3 className="font-semibold mb-2">🧭 Navigation</h3>
              <div className="space-y-2">
                <a
                  href="/"
                  className="block text-blue-600 dark:text-blue-400 hover:underline">
                  ← Back to Home
                </a>
                <a
                  href="http://localhost:3000"
                  className="block text-blue-600 dark:text-blue-400 hover:underline">
                  → Go to App
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PocketBaseTestPage;
